export type CuratorProof = {
  type: 'onchain' | 'document'
  value: string
}

export type CustomReferralWallet = {
  walletAddress: string
  telegram: string
  promoCode: string
  discountPercentage: string
  kickbackPercentage: string
}

export type EquityBasePdf = {
  name: string
  key: string
}

export type UrlEntry = {
  id: string
  type: string
  url: string
}

export type TokenInfo = {
  address: string
  symbol: string
  name: string
  decimals: string
}

export type EquityFormData = {
  // Overview
  projectName: string
  projectDescription: string
  projectMemo: string
  logoUrl: string
  categoryTags: string[]
  websiteUrl: string
  xUrl: string
  discordUrl: string
  otherUrls: UrlEntry[]
  pitchDeckUrl: string
  researchReportUrl: string
  requestResearchSupport: boolean
  dealType: string
  jurisdiction: string
  targetRaise: string
  fdv: string

  // Equity vehicle
  vehicleCompanyName: string
  vehicleDirectorName: string
  vehicleSignatoryName: string
  vehicleSignatoryTitle: string
  vehicleRegisteredAddress: string
  vehicleCompanyNo: string

  // Investment terms
  minRaiseThreshold: string
  minInvestmentPerUser: string
  useMaxDeposit: boolean
  maxDeposit: string
  depositChainId: string
  paymentToken: TokenInfo
  distributionChainId: string
  projectToken: TokenInfo
  maxCap: string
  tokenPrice: string
  cliffDays: string
  vestingDays: string
  tgeUnlockPct: string

  // Timeline (datetime-local strings, e.g. "2026-05-11T08:50")
  depositStarts: string
  depositEnds: string
  shortlistNotified: string
  signAgreement: string
  firstUnlock: string
  nftTransferable: string

  // Base PDFs
  equityBasePdfs: EquityBasePdf[]

  // Referral
  referralEnabled: boolean
  referralGeneralDiscount: string
  referralGeneralKickback: string
  referralCustomWallets: CustomReferralWallet[]
  referralDiscount: string

  // Funds & royalties
  projectReceivingWallet: string
  curatorCommitmentAmount: string
  curatorProofs: CuratorProof[]
  curatorFeeBips: string
  curatorWallet: string
  projectRoyaltyBips: string
  curatorRoyaltyBips: string

  // Visibility & eligibility
  visibility: 'public' | 'whitelisted'
  blockedCountries: string[]
  investorEligibility: string[]

  // Accessory services
  accessoryMarketing: boolean
  accessoryKolCampaigns: boolean
  accessoryMarketMaking: boolean
  accessoryTreasuryManagement: boolean
  accessoryOtcDeals: boolean
  accessoryAuditing: boolean
  accessoryHrRecruitment: boolean
  accessoryCexListing: boolean
  accessoryOther: string

  // NFT
  nftName: string
  nftSymbol: string
}

const emptyToken: TokenInfo = {
  address: '',
  symbol: '',
  name: '',
  decimals: '',
}

export const initialEquityFormData: EquityFormData = {
  projectName: '',
  projectDescription: '',
  projectMemo: '',
  logoUrl: '',
  categoryTags: [],
  websiteUrl: '',
  xUrl: '',
  discordUrl: '',
  otherUrls: [],
  pitchDeckUrl: '',
  researchReportUrl: '',
  requestResearchSupport: false,
  dealType: '',
  jurisdiction: '',
  targetRaise: '',
  fdv: '',

  vehicleCompanyName: '',
  vehicleDirectorName: '',
  vehicleSignatoryName: '',
  vehicleSignatoryTitle: '',
  vehicleRegisteredAddress: '',
  vehicleCompanyNo: '',

  minRaiseThreshold: '',
  minInvestmentPerUser: '',
  useMaxDeposit: false,
  maxDeposit: '',
  depositChainId: '',
  paymentToken: { ...emptyToken },
  distributionChainId: '',
  projectToken: { ...emptyToken },
  maxCap: '',
  tokenPrice: '',
  cliffDays: '',
  vestingDays: '',
  tgeUnlockPct: '',

  depositStarts: '',
  depositEnds: '',
  shortlistNotified: '',
  signAgreement: '',
  firstUnlock: '',
  nftTransferable: '',

  equityBasePdfs: [],

  referralEnabled: false,
  referralGeneralDiscount: '',
  referralGeneralKickback: '',
  referralCustomWallets: [],
  referralDiscount: '',

  projectReceivingWallet: '',
  curatorCommitmentAmount: '',
  curatorProofs: [],
  curatorFeeBips: '',
  curatorWallet: '',
  projectRoyaltyBips: '',
  curatorRoyaltyBips: '',

  visibility: 'public',
  blockedCountries: [],
  investorEligibility: [],

  accessoryMarketing: false,
  accessoryKolCampaigns: false,
  accessoryMarketMaking: false,
  accessoryTreasuryManagement: false,
  accessoryOtcDeals: false,
  accessoryAuditing: false,
  accessoryHrRecruitment: false,
  accessoryCexListing: false,
  accessoryOther: '',

  nftName: '',
  nftSymbol: '',
}

export const DEAL_TYPE_OPTIONS = [
  'seed',
  'seriesA',
  'seriesB',
  'seriesC',
  'bridge',
] as const

export const INVESTOR_ELIGIBILITY_OPTIONS = [
  'KYC_nonAccredited',
  'KYC_accredited',
  'KYC_qualifiedPurchaser',
] as const
